import type { BookingBlockedIssue } from './bookingDecision';
import type { BookingMomentSection } from './bookingMoment';
import type {
  RuteroOfferOptionsSelection,
  RuteroOfferOptionsSelectionPreview,
} from '@bandido/shared/types';
import { offerOptionsSelectionPayload } from './offerOptionsSelection';
import type { OptionalItemsHoldError } from './offerOptionalItems';
import type {
  OfferOptionsRequest,
  TourQuoteRequirement,
} from './queries';

export type TourBookingStage = 'availability' | 'passengers';

export interface TourBookingFlowState {
  stage: TourBookingStage;
  /**
   * Identidad única de este refresh explícito. Se conserva en retries de la
   * misma visita y nunca colisiona al cerrar/reabrir la misma oferta.
   */
  quoteRefreshId?: string;
}

export interface FrozenTourQuote {
  /** Seleccion exacta que produjo el quote, incluido su token opaco. */
  options: RuteroOfferOptionsSelection;
  /** Total y desglose que corresponden al mismo token opaco. */
  preview: RuteroOfferOptionsSelectionPreview;
  /** Edades no personales exactas que Rutero validó para esa selección. */
  ages?: number[];
  /** Día local publicado por la sesión; es la referencia para reconciliar edad. */
  serviceDate?: string;
}

export interface TourOptionalItemsHoldRecovery {
  error: OptionalItemsHoldError;
  flow: TourBookingFlowState;
  frozenQuote: null;
}

export interface ScopedTourQuoteRequirements {
  quoteAuthority: string;
  sessionId: string;
  requirements: TourQuoteRequirement[];
}

/**
 * Aprende requisitos solo del request que sigue visible. Un error tardío de
 * otra provenance, sesión o selección no puede modificar el formulario actual.
 */
export function captureTourQuoteRequirements(input: {
  current: ScopedTourQuoteRequirements | null;
  quoteAuthority: string | null | undefined;
  requestSessionId: string | null | undefined;
  liveQuoteAuthority: string | null | undefined;
  liveSessionId: string;
  requestMatchesLive: boolean;
  requirements: readonly TourQuoteRequirement[];
}): ScopedTourQuoteRequirements | null {
  const {
    current,
    quoteAuthority,
    requestSessionId,
    liveQuoteAuthority,
    liveSessionId,
    requestMatchesLive,
    requirements,
  } = input;
  if (
    !quoteAuthority ||
    !requestSessionId ||
    quoteAuthority !== liveQuoteAuthority ||
    requestSessionId !== liveSessionId ||
    !requestMatchesLive ||
    requirements.length === 0
  ) {
    return current;
  }
  const previous =
    current?.quoteAuthority === quoteAuthority && current.sessionId === requestSessionId
      ? current.requirements
      : [];
  if (
    previous.length > 0 &&
    requirements.every((requirement) => previous.includes(requirement))
  ) {
    return current;
  }
  return {
    quoteAuthority,
    sessionId: requestSessionId,
    requirements: [...new Set([...previous, ...requirements])],
  };
}

export function activeTourQuoteRequirements(
  scoped: ScopedTourQuoteRequirements | null,
  quoteAuthority: string | null | undefined,
  sessionId: string,
): TourQuoteRequirement[] {
  if (!scoped) return [];
  return scoped.quoteAuthority === quoteAuthority && scoped.sessionId === sessionId
    ? scoped.requirements
    : [];
}

/**
 * Reconsulta options únicamente para el rechazo contractual que todavía
 * pertenece a la selección visible. Conserva la misma sesión y composición;
 * Rutero devuelve el catálogo faltante y la UI no elige ningún sustituto.
 */
export function tourQuoteRequirementsOptionsRequest(input: {
  quoteAuthority: string | null | undefined;
  requestSessionId: string | null | undefined;
  liveQuoteAuthority: string | null | undefined;
  liveSessionId: string;
  requestMatchesLive: boolean;
  requirements: readonly TourQuoteRequirement[];
  partySize?: number;
  ages?: readonly number[];
  selection: RuteroOfferOptionsSelection;
}): OfferOptionsRequest | null {
  const {
    quoteAuthority,
    requestSessionId,
    liveQuoteAuthority,
    liveSessionId,
    requestMatchesLive,
    requirements,
    partySize,
    ages,
    selection,
  } = input;
  if (
    !quoteAuthority ||
    !requestSessionId ||
    quoteAuthority !== liveQuoteAuthority ||
    requestSessionId !== liveSessionId ||
    !requestMatchesLive ||
    requirements.length === 0
  ) {
    return null;
  }
  return {
    provenanceToken: quoteAuthority,
    ...(partySize ? { partySize } : {}),
    ...(ages?.length ? { distribution: { ages: [...ages] } } : {}),
    selection: offerOptionsSelectionPayload({
      ...selection,
      sessionId: requestSessionId,
      quoteToken: undefined,
    }),
  };
}

/** Rechazos limpios que invalidan el token pero conservan la sesión elegida. */
export function tourQuoteMustRefreshAfterHoldError(code: string | undefined): boolean {
  return code === 'RUTERO_HOLD_QUOTE_MISMATCH' || code === 'QUOTE_INPUT_REQUIRED';
}

export const INITIAL_TOUR_BOOKING_FLOW: TourBookingFlowState = {
  stage: 'availability',
};

/** Cada apertura del modal empieza una visita de quote aislada de las demás. */
export function startTourBookingVisit(
  createRefreshId: () => string,
): TourBookingFlowState {
  return {
    stage: 'availability',
    quoteRefreshId: createRefreshId(),
  };
}

export function continueTourBookingFlow(
  state: TourBookingFlowState,
  hasAuthoritativeQuote: boolean,
): TourBookingFlowState {
  return hasAuthoritativeQuote ? { ...state, stage: 'passengers' } : state;
}

export function reviseTourAvailability(
  createRefreshId: () => string,
): TourBookingFlowState {
  return startTourBookingVisit(createRefreshId);
}

/**
 * Un rechazo de adicionales pertenece al primer paso: vuelve a exponer los
 * controles y descarta el quote congelado, pero deja intactos los pasajeros.
 */
export function recoverTourOptionalItemsHoldError(
  error: OptionalItemsHoldError,
  createRefreshId: () => string,
): TourOptionalItemsHoldRecovery {
  return {
    error,
    flow: reviseTourAvailability(createRefreshId),
    frozenQuote: null,
  };
}

/**
 * Frontera quote -> hold: copia la seleccion autoritativa para que el paso de
 * pasajeros no pueda reconstruirla a partir de controles vivos.
 */
export function freezeTourQuote(
  selection: RuteroOfferOptionsSelection,
  quoteToken: string | undefined,
  preview: RuteroOfferOptionsSelectionPreview | undefined,
  ages: readonly number[] | null,
  serviceDate?: string,
): FrozenTourQuote | null {
  if (!quoteToken || preview?.state !== 'priced' || !preview.total) return null;
  return {
    options: offerOptionsSelectionPayload({
      ...selection,
      quoteToken,
      ticketQuantities: selection.ticketQuantities?.map((entry) => ({ ...entry })),
      accommodationIds: selection.accommodationIds
        ? [...selection.accommodationIds]
        : undefined,
      optionalItemCodes: selection.optionalItemCodes
        ? [...selection.optionalItemCodes]
        : undefined,
      supplements: selection.supplements?.map((entry) => ({ ...entry })),
      occupancy: selection.occupancy ? { ...selection.occupancy } : undefined,
    }),
    preview: { ...preview, quoteToken },
    ...(ages ? { ages: [...ages] } : {}),
    ...(serviceDate ? { serviceDate } : {}),
  };
}

const ISO_CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function calendarDateParts(value: string | undefined) {
  const match = value?.match(ISO_CALENDAR_DATE);
  if (!match) return null;
  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
}

/** Edad calendario en el día local del servicio, sin reloj ni zona del browser. */
export function tourPassengerAgeOnServiceDate(
  birthDate: string | undefined,
  serviceDate: string | undefined,
): number | null {
  const birth = calendarDateParts(birthDate);
  const service = calendarDateParts(serviceDate);
  if (!birth || !service) return null;
  let age = service.year - birth.year;
  if (
    service.month < birth.month ||
    (service.month === birth.month && service.day < birth.day)
  ) {
    age -= 1;
  }
  return age >= 0 && age <= 120 ? age : null;
}

export interface TourBirthDateAgeReconciliation {
  /** Edades para una recotización: las completas corrigen; las demás conservan quote. */
  ages: number[] | null;
  mismatchedIndexes: number[];
}

/**
 * Una fecha parcial o inválida todavía no dice nada sobre la edad. Solo una
 * fecha completa, comparada con el día de la sesión, puede invalidar el quote.
 */
export function reconcileTourBirthDateAges(
  quote: FrozenTourQuote | null,
  passengers: readonly { birthDate?: string }[],
): TourBirthDateAgeReconciliation {
  if (!quote?.ages) return { ages: null, mismatchedIndexes: [] };
  if (!quote.serviceDate) {
    return { ages: [...quote.ages], mismatchedIndexes: [] };
  }
  const derivedAges = quote.ages.map((_quotedAge, index) =>
    tourPassengerAgeOnServiceDate(
      passengers[index]?.birthDate,
      quote.serviceDate,
    ));
  if (derivedAges.some((age) => age === null)) {
    return { ages: null, mismatchedIndexes: [] };
  }
  const ages = derivedAges as number[];
  return {
    ages,
    mismatchedIndexes: ages.flatMap((age, index) =>
      age === quote.ages?.[index] ? [] : [index]),
  };
}

export function frozenTourQuoteMatchesAges(
  quote: FrozenTourQuote | null,
  ages: readonly number[] | null,
): boolean {
  if (!quote) return false;
  if (!quote.ages) return ages === null;
  return Boolean(
    ages &&
    quote.ages.length === ages.length &&
    quote.ages.every((age, index) => age === ages[index]),
  );
}

/** Solo expone al hold la seleccion congelada que aun coincide con las edades. */
export function frozenTourOptionsForHold(
  quote: FrozenTourQuote | null,
  ages: readonly number[] | null,
): RuteroOfferOptionsSelection | undefined {
  return frozenTourQuoteMatchesAges(quote, ages)
    ? quote?.options
    : undefined;
}

/** La primera etapa solo descubre y cotiza; todavía no pide datos de hold. */
export function tourSectionsForStage(
  sections: readonly BookingMomentSection[],
  stage: TourBookingStage,
): BookingMomentSection[] {
  return sections.filter((section) =>
    stage === 'availability'
      ? section !== 'passengers'
      : section !== 'optionalItems');
}

/**
 * El CTA de disponibilidad no es un hold: solo lo bloquea la selección/quote,
 * nunca los nombres y datos que se completan en el paso siguiente.
 */
export function tourAvailabilityBlockedIssues(
  blocked: readonly BookingBlockedIssue[],
): BookingBlockedIssue[] {
  return blocked.filter(
    ({ reason }) => reason === 'options' || reason === 'loadingOptions',
  );
}
